import 'flowtoken/dist/styles.css';
import { Button } from '@ctw/shared/components/button';
import { useInterval } from '@ctw/shared/hooks/use-interval';
import { tw } from '@ctw/shared/utils/tailwind';
import { AnimatedMarkdown } from 'flowtoken';
import { compact } from 'lodash-es';
import {
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

type CustomStreamComponents = Record<
  string,
  (
    props: {
      animateText: (content: ReactNode) => ReactNode;
      children: ReactNode;
    } & HTMLAttributes<HTMLDivElement>,
  ) => ReactNode
>;

type ContentBlockTokenizationMode = 'block' | 'word';

export interface ContentBlockMarkdown {
  markdown: string;
  tokenizationMode: ContentBlockTokenizationMode;
  /** When false, the whole block appears in one step; when true, body is tokenized and revealed progressively. */
  streaming: boolean;
  prefix?: string;
  suffix?: string;
  reactNode?: never;
}

const isMarkdownContentBlock = (block: ContentBlock): block is ContentBlockMarkdown => {
  return Object.hasOwn(block, 'markdown');
};

export interface ContentBlockReactNode {
  reactNode: ReactNode;
  /** When false, the node appears without animation; when true, "stream" the text in. */
  streaming: boolean;
  prefix?: string;
  suffix?: string;
  markdown?: never;
}

const isReactNodeContentBlock = (block: ContentBlock): block is ContentBlockReactNode => {
  return Object.hasOwn(block, 'reactNode');
};

export type ContentBlock = ContentBlockMarkdown | ContentBlockReactNode;

const IS_UNICODE_WHITESPACE = /\s/u;

const processMarkdown = (markdown: string): string => {
  return markdown.replaceAll(/\[([^\]]+)\]/g, '<citation>$1</citation>');
};

interface BlockTokenRange {
  start: number;
  endExclusive: number;
  streaming: boolean;
}

/**
 * Word mode: split on whitespace outside of HTML tags so `<p>word word</p>` streams word-by-word
 * while tags (e.g. `<citation>`, attributes) stay atomic.
 */
const splitBodyIntoWordTokens = (processedBody: string): Array<string> => {
  if (processedBody.length === 0) {
    return [];
  }

  const tokens: Array<string> = [];
  let i = 0;

  while (i < processedBody.length) {
    const ch = processedBody[i];

    if (ch === '<') {
      const end = processedBody.indexOf('>', i);
      if (end === -1) {
        tokens.push(processedBody.slice(i));
        break;
      }
      tokens.push(processedBody.slice(i, end + 1));
      i = end + 1;
      continue;
    }

    if (IS_UNICODE_WHITESPACE.test(ch)) {
      i += 1;
      continue;
    }

    let j = i;
    while (j < processedBody.length) {
      const cj = processedBody[j];
      if (IS_UNICODE_WHITESPACE.test(cj) || cj === '<') {
        break;
      }
      j += 1;
    }

    if (j > i) {
      tokens.push(processedBody.slice(i, j));
    }
    i = j;
  }

  return tokens.filter((t) => t.length > 0);
};

const buildTokensWithRanges = (
  contentBlocks: Array<ContentBlock>,
): {
  tokens: Array<string>;
  ranges: Array<BlockTokenRange>;
} => {
  const tokens: Array<string> = [];
  const ranges: Array<BlockTokenRange> = [];

  for (const block of contentBlocks) {
    const start = tokens.length;

    if (isMarkdownContentBlock(block)) {
      const processedBody = processMarkdown(block.markdown);

      if (!block.streaming) {
        const chunk = compact([block.prefix, processedBody, block.suffix]).join('');
        if (chunk.length > 0) {
          tokens.push(chunk);
        }
      } else if (block.tokenizationMode === 'block') {
        const parts = compact([block.prefix, processedBody, block.suffix]);
        tokens.push(...parts);
      } else {
        const words = splitBodyIntoWordTokens(processedBody);
        tokens.push(...compact([block.prefix, ...words, block.suffix]));
      }

      ranges.push({
        start,
        endExclusive: tokens.length,
        streaming: block.streaming,
      });
    } else if (isReactNodeContentBlock(block)) {
      const markup = renderToStaticMarkup(block.reactNode);
      const chunk = compact([block.prefix, markup, block.suffix]).join('');
      if (chunk.length > 0) {
        tokens.push(chunk);
      }
      ranges.push({
        start,
        endExclusive: tokens.length,
        streaming: block.streaming,
      });
    }
  }

  return { tokens, ranges };
};

const buildFrozenFullContent = (flatTokens: Array<string>): string => {
  return flatTokens.join(' ');
};

interface ResolvedCitation {
  onClick: () => void;
}

type CitationHandler = (citationId: string) => ResolvedCitation;

export interface StreamingContentProps extends PropsWithChildren {
  contentBlocks: Array<ContentBlock>;
  /** When this changes, frozen non-streaming snapshots and presentation reset. */
  streamKey?: string;
  enabled?: boolean;
  tokensPerSecond?: number;
  handleCitation?: CitationHandler;
  onAllContentPresented?: () => void;
}

export const StreamingContent = ({
  contentBlocks,
  streamKey = '',
  handleCitation,
  onAllContentPresented,
  enabled = false,
  tokensPerSecond = 15,
}: StreamingContentProps) => {
  const frozenMarkdownByBlockIndexRef = useRef<Map<number, string>>(new Map());
  const frozenReactNodeByBlockIndexRef = useRef<Map<number, ReactNode>>(new Map());
  const streamKeyRef = useRef(streamKey);

  const effectiveContentBlocks = useMemo(() => {
    if (streamKeyRef.current !== streamKey) {
      streamKeyRef.current = streamKey;
      frozenMarkdownByBlockIndexRef.current = new Map();
      frozenReactNodeByBlockIndexRef.current = new Map();
    }

    return contentBlocks.map((block, index) => {
      if (isMarkdownContentBlock(block) && !block.streaming) {
        const frozen = frozenMarkdownByBlockIndexRef.current.get(index);
        if (frozen !== undefined) {
          return { ...block, markdown: frozen };
        }

        frozenMarkdownByBlockIndexRef.current.set(index, block.markdown);
        return block;
      }

      if (isReactNodeContentBlock(block) && !block.streaming) {
        const frozen = frozenReactNodeByBlockIndexRef.current.get(index);
        if (frozen !== undefined) {
          return { ...block, reactNode: frozen };
        }

        frozenReactNodeByBlockIndexRef.current.set(index, block.reactNode);
        return block;
      }

      return block;
    });
  }, [contentBlocks, streamKey]);

  const { tokens: availableTokens, ranges: blockTokenRanges } = useMemo(
    () => buildTokensWithRanges(effectiveContentBlocks),
    [effectiveContentBlocks],
  );

  const [presentedTokenCount, setPresentedTokenCount] = useState(0);
  const [useFrozenFinal, setUseFrozenFinal] = useState(false);
  const finalContentRef = useRef<string | null>(null);
  const prevAvailableTokenLengthRef = useRef(0);
  const presentedTokenCountRef = useRef(0);

  useEffect(() => {
    presentedTokenCountRef.current = presentedTokenCount;
  }, [presentedTokenCount]);

  const customStreamComponents: CustomStreamComponents = useMemo(
    () => ({
      citation: ({ animateText, children }) => {
        if (typeof children === 'string') {
          const resolvedCitation = handleCitation?.(children);
          if (resolvedCitation) {
            return (
              <Button
                type="button"
                variant="link"
                className={tw`inline`}
                onClick={resolvedCitation.onClick}
              >
                <sup className="ml-0.5 cursor-help text-content-subtle text-xs">
                  {animateText(`[${children}]`)}
                </sup>
              </Button>
            );
          }
        }

        return <></>;
      },
    }),
    [handleCitation],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset presentation when the stream identity changes
  useEffect(() => {
    setPresentedTokenCount(0);
    setUseFrozenFinal(false);
    finalContentRef.current = null;
    prevAvailableTokenLengthRef.current = 0;
  }, [streamKey]);

  useLayoutEffect(() => {
    for (const range of blockTokenRanges) {
      if (range.endExclusive <= range.start) {
        continue;
      }

      const lastIndexInBlock = range.endExclusive - 1;
      const prevCount = presentedTokenCountRef.current;
      const priorTokensReady = range.start === 0 || prevCount >= range.start - 1;

      if (!range.streaming && priorTokensReady) {
        setPresentedTokenCount((current) => Math.max(current, lastIndexInBlock));
      }
    }
  }, [blockTokenRanges]);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const prevLen = prevAvailableTokenLengthRef.current;
    const len = availableTokens.length;

    if (len < prevLen) {
      prevAvailableTokenLengthRef.current = len;
      setPresentedTokenCount((current) => Math.min(current, Math.max(0, len - 1)));
    } else if (len > prevLen && prevLen > 0) {
      if (useFrozenFinal) {
        setUseFrozenFinal(false);
      }
      setPresentedTokenCount((previous) => Math.min(previous, len - 1));
    }

    prevAvailableTokenLengthRef.current = len;
  }, [enabled, availableTokens, useFrozenFinal]);

  useEffect(() => {
    if (!enabled) {
      setUseFrozenFinal(false);
      finalContentRef.current = null;
      setPresentedTokenCount(0);
      prevAvailableTokenLengthRef.current = 0;
    }
  }, [enabled]);

  const advancePresentedCount = (prevTokenCount: number): number => {
    if (prevTokenCount >= availableTokens.length - 1) {
      return prevTokenCount;
    }
    let next = prevTokenCount + 1;

    for (const range of blockTokenRanges) {
      if (next >= range.start && next < range.endExclusive) {
        if (!range.streaming) {
          next = range.endExclusive - 1;
        }
        break;
      }
    }

    return next;
  };

  useInterval({
    intervalInMilliseconds: 1000.0 / tokensPerSecond,
    intervalFunction: () => {
      if (enabled && !useFrozenFinal) {
        setPresentedTokenCount((prev) => {
          if (prev >= availableTokens.length - 1) {
            return prev;
          }
          return advancePresentedCount(prev);
        });
      }
    },
  });

  useEffect(() => {
    if (
      !enabled ||
      useFrozenFinal ||
      availableTokens.length === 0 ||
      presentedTokenCount < availableTokens.length - 1
    ) {
      return;
    }

    finalContentRef.current = buildFrozenFullContent(availableTokens);
    setUseFrozenFinal(true);
    setTimeout(() => {
      onAllContentPresented?.();
    }, 0);
  }, [enabled, presentedTokenCount, availableTokens, onAllContentPresented, useFrozenFinal]);

  const flatExclusiveEnd = useFrozenFinal
    ? availableTokens.length
    : Math.min(availableTokens.length, presentedTokenCount + 1);

  const blockMarkdownSlices = useMemo(() => {
    return blockTokenRanges.map((range) => {
      if (range.endExclusive <= range.start) {
        return { markdown: '', streaming: range.streaming };
      }
      const sliceEnd = Math.min(range.endExclusive, flatExclusiveEnd);
      const raw =
        sliceEnd > range.start ? availableTokens.slice(range.start, sliceEnd).join(' ') : '';
      const interBlockSpace = range.start > 0 && raw.length > 0 ? ' ' : '';
      return {
        markdown: interBlockSpace + raw,
        streaming: range.streaming,
      };
    });
  }, [blockTokenRanges, availableTokens, flatExclusiveEnd]);

  // flowtoken wraps diff chunks in `display: inline-block` + `pre-wrap`, which can force line breaks
  // after inline custom components (citations). Normalize spans inside prose to true inline flow.
  return (
    <div
      className={tw`[&_p_span]:!inline [&_p_span]:!whitespace-normal [&_p_button]:align-baseline`}
    >
      {blockMarkdownSlices.map((slice, blockIndex) =>
        slice.markdown.length === 0 ? null : (
          <AnimatedMarkdown
            // biome-ignore lint/suspicious/noArrayIndexKey: content block order is fixed per streamKey; streamKey remounts the tree
            key={`${streamKey}:${blockIndex}`}
            content={slice.markdown}
            sep="diff"
            animation={slice.streaming ? 'fadeIn' : ''}
            animationTimingFunction="easeOut"
            animationDuration="0.6s"
            customComponents={customStreamComponents}
          />
        ),
      )}
    </div>
  );
};
